import { useState } from 'react'
import type { Account, Contact as ContactData } from '../api/schema.js'
import { copyText } from '../lib/clipboard.js'
import { roleTones, type RoleTone } from '../styles/roleTones.js'
import styles from './GiftAccounts.module.css'

export interface GiftAccountsProps {
  contact: ContactData
  onCopied: (message: string) => void
}

interface PanelProps {
  role: string
  tone: RoleTone
  accounts: Account[]
  onCopied: (message: string) => void
}

function Panel({ role, tone, accounts, onCopied }: PanelProps) {
  const [open, setOpen] = useState(false)

  async function handleCopy(account: Account) {
    await copyText(`${account.bank} ${account.number}`)
    onCopied('계좌번호가 복사되었어요')
  }

  return (
    <div className={styles.panel} style={tone}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={styles.headerLeft}>
          {/* 공백 텍스트는 flex 컨테이너에서 렌더링되지 않지만, 버튼의 접근 가능한 이름이
              CSS 레이아웃과 무관하게 "신랑측 계좌번호 보기"로 띄어 읽히게 한다. */}
          <span className={styles.badge}>{role}</span>{' '}
          <span className={styles.headerTitle}>계좌번호 보기</span>
        </span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>
      <div className={styles.collapse} style={{ maxHeight: open ? '420px' : '0px' }}>
        <div className={styles.rows}>
          {accounts.map((account, index) => (
            <div key={`${account.role}-${index}`} className={styles.row}>
              <div>
                <div className={styles.rowRole}>
                  {account.role} {account.name}
                </div>
                <div className={styles.rowBank}>
                  {account.bank} {account.number}
                </div>
              </div>
              <button type="button" className={styles.copyBtn} onClick={() => void handleCopy(account)}>
                복사
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** 계좌도 전화번호처럼 API에서만 온다 — 없으면 그 쪽(혹은 섹션 전체)을 그리지 않는다. */
export function GiftAccounts({ contact, onCopied }: GiftAccountsProps) {
  const groomAccounts = contact.accounts?.groom ?? []
  const brideAccounts = contact.accounts?.bride ?? []
  if (groomAccounts.length === 0 && brideAccounts.length === 0) return null

  return (
    <div className={styles.wrap}>
      <div className={styles.divider}>
        <span className={styles.line} />
        <span className={styles.dot} />
        <span className={styles.line} />
      </div>
      <div className={styles.eyebrow}>with love</div>
      <h2 className={styles.title}>마음 전하실 곳</h2>
      <p className={styles.lead}>
        마음을 전하고 싶으신 분들을 위해
        <br />
        계좌번호를 안내드립니다.
      </p>
      <div className={styles.list}>
        {groomAccounts.length > 0 && (
          <Panel role="신랑측" tone={roleTones.groom} accounts={groomAccounts} onCopied={onCopied} />
        )}
        {brideAccounts.length > 0 && (
          <Panel role="신부측" tone={roleTones.bride} accounts={brideAccounts} onCopied={onCopied} />
        )}
      </div>
    </div>
  )
}
